import type { SectorId, WorkspaceDocument } from '@myplayer/schema';
import { IconCards, IconCheck, IconEdit, IconPlus } from '@myplayer/ui';
import type { FormEvent, ReactElement } from 'react';
import {
  ActionOverflowMenu,
  Badge,
  Button,
  Input,
  ModalDialog,
  Select,
  Tag,
  useContextMenu,
} from './components/ui/primitives.js';
import { buildWorkspaceContextMenuItems } from './context-menu-items.js';
import { WORKSPACE_SECTOR_OPTIONS } from './workspaces-firestore.js';

export interface WorkspaceRowProps {
  readonly ws: WorkspaceDocument;
  readonly isCurrent: boolean;
  readonly canUpdateWorkspace: boolean;
  readonly submitting: boolean;
  readonly onStartRename: (ws: WorkspaceDocument) => void;
  readonly onArchive: (ws: WorkspaceDocument) => void;
  readonly onSelectWorkspace?: ((ws: WorkspaceDocument) => void) | undefined;
}

export function WorkspaceRow({
  ws,
  isCurrent,
  canUpdateWorkspace,
  submitting,
  onStartRename,
  onArchive,
  onSelectWorkspace,
}: WorkspaceRowProps): ReactElement {
  const { bindContextMenu, contextMenuElement } = useContextMenu();
  const sectorLabel =
    WORKSPACE_SECTOR_OPTIONS.find((opt) => opt.value === ws.sector)?.label ?? ws.sector;
  const statusLabel = ws.status === 'ACTIVE' ? 'Active' : 'Archived';

  return (
    <tr
      data-testid={`workspace-row-${ws.workspaceId}`}
      onContextMenu={bindContextMenu(() =>
        buildWorkspaceContextMenuItems({
          ws,
          isCurrent,
          canUpdateWorkspace,
          submitting,
          onStartRename,
          onArchive,
          onSelectWorkspace,
        }),
      )}
      className="h-12 transition-colors hover:bg-surface-muted"
    >
      <td className="px-3.5 py-2.5 align-middle">
        <span className="font-medium text-fg">{ws.name}</span>
        {contextMenuElement}
      </td>
      <td className="px-3.5 py-2.5 align-middle">
        <Tag variant="outline">{sectorLabel}</Tag>
      </td>
      <td className="px-3.5 py-2.5 align-middle">
        <Badge variant={ws.status === 'ACTIVE' ? 'success' : 'outline'}>{statusLabel}</Badge>
      </td>
      {canUpdateWorkspace && (
        <td className="px-3.5 py-2.5 align-middle text-right">
          {ws.status !== 'ARCHIVED' && (
            <div className="flex items-center justify-end gap-1.5">
              <Button
                type="button"
                variant="secondary"
                size="xs"
                disabled={submitting}
                aria-label="Rename"
                onClick={() => onStartRename(ws)}
              >
                <IconEdit />
                <span>Edit</span>
              </Button>
              {!isCurrent && (
                <ActionOverflowMenu
                  label={`More actions for ${ws.name}`}
                  size="xs"
                  disabled={submitting}
                  data-testid={`workspace-overflow-${ws.workspaceId}`}
                >
                  <Button
                    type="button"
                    variant="destructive"
                    size="xs"
                    disabled={submitting}
                    onClick={() => onArchive(ws)}
                  >
                    Archive
                  </Button>
                </ActionOverflowMenu>
              )}
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

export function CreateWorkspaceModal({
  open,
  orgId,
  newName,
  setNewName,
  newSector,
  setNewSector,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: {
  readonly open: boolean;
  readonly orgId: string;
  readonly newName: string;
  readonly setNewName: (val: string) => void;
  readonly newSector: SectorId;
  readonly setNewSector: (val: SectorId) => void;
  readonly submitting: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (e: FormEvent) => void;
}): ReactElement {
  return (
    <ModalDialog
      open={open}
      title="Create Workspace"
      subtitle={`Create a new workspace inside ${orgId}`}
      icon={<IconCards />}
      presentation="center"
      maxWidthClassName="max-w-xl"
      submitting={submitting}
      onClose={onClose}
      data-testid="create-workspace-modal"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-workspace-form"
            variant="primary"
            size="sm"
            disabled={submitting}
          >
            <IconPlus />
            <span>Create Workspace</span>
          </Button>
        </>
      }
    >
      <form id="create-workspace-form" onSubmit={onSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="new-workspace-name"
            className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
          >
            New Workspace Name
          </label>
          <Input
            id="new-workspace-name"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="EU Healthcare"
            required
          />
        </div>
        <div>
          <label
            htmlFor="new-workspace-sector"
            className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
          >
            Primary Sector
          </label>
          <Select
            id="new-workspace-sector"
            aria-label="Primary Sector"
            value={newSector}
            onChange={(e) => setNewSector(e.target.value as SectorId)}
          >
            {WORKSPACE_SECTOR_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </div>
        {errorMessage !== null && (
          <p
            role="alert"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
          >
            {errorMessage}
          </p>
        )}
      </form>
    </ModalDialog>
  );
}

export function RenameWorkspaceModal({
  target,
  renameDraft,
  setRenameDraft,
  submitting,
  errorMessage,
  onClose,
  onSave,
}: {
  readonly target: WorkspaceDocument | null;
  readonly renameDraft: string;
  readonly setRenameDraft: (val: string) => void;
  readonly submitting: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSave: (ws: WorkspaceDocument) => void;
}): ReactElement {
  return (
    <ModalDialog
      open={target !== null}
      title="Rename Workspace"
      subtitle={target ? `Update display name for ${target.workspaceId}` : ''}
      icon={<IconEdit />}
      presentation="center"
      maxWidthClassName="max-w-xl"
      submitting={submitting}
      onClose={onClose}
      data-testid="rename-workspace-modal"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={submitting}
            onClick={() => {
              if (target) onSave(target);
            }}
          >
            <IconCheck />
            <span>Save Name</span>
          </Button>
        </>
      }
    >
      {target && (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="rename-workspace-input"
              className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
            >
              New name for {target.workspaceId}
            </label>
            <Input
              id="rename-workspace-input"
              aria-label={`New name for ${target.workspaceId}`}
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onClose();
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  onSave(target);
                }
              }}
            />
          </div>
          {errorMessage !== null && (
            <p
              role="alert"
              className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] text-status-error"
            >
              {errorMessage}
            </p>
          )}
        </div>
      )}
    </ModalDialog>
  );
}
